import { apiHealth, apiJson } from "../api/client";
import {
  hashPassword,
  isValidEmail,
  isValidPassword,
  normalizeEmail,
  randomSalt,
  verifyPassword,
} from "./password";
import type { AuthResult, AuthSession, AuthUser } from "./types";

const USERS_KEY = "un8.auth.users.v1";
const SESSION_KEY = "un8.auth.session.v1";
const RECOVERY_SHOWN_KEY = "un8.auth.recoveryShown.v1";
const MODE_KEY = "un8.auth.mode.v1";

type Listener = () => void;
const listeners = new Set<Listener>();

function notify() {
  for (const fn of listeners) {
    try {
      fn();
    } catch {
      /* ignore */
    }
  }
}

function readUsers(): Record<string, AuthUser> {
  try {
    const raw = localStorage.getItem(USERS_KEY);
    if (!raw) return {};
    return JSON.parse(raw) as Record<string, AuthUser>;
  } catch {
    return {};
  }
}

function writeUsers(users: Record<string, AuthUser>) {
  try {
    localStorage.setItem(USERS_KEY, JSON.stringify(users));
  } catch {
    /* quota */
  }
}

function readSession(): AuthSession | null {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as AuthSession;
    if (!parsed?.userId || !parsed?.email) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeSession(session: AuthSession | null) {
  try {
    if (!session) localStorage.removeItem(SESSION_KEY);
    else localStorage.setItem(SESSION_KEY, JSON.stringify(session));
  } catch {
    /* quota */
  }
  notify();
}

function setMode(mode: "local" | "server") {
  try {
    localStorage.setItem(MODE_KEY, mode);
  } catch {
    /* ignore */
  }
}

export function getAuthMode(): "local" | "server" {
  try {
    const m = localStorage.getItem(MODE_KEY);
    if (m === "server" || m === "local") return m;
  } catch {
    /* ignore */
  }
  return "local";
}

function newUserId(): string {
  return `user_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 9)}`;
}

function toPublic(user: AuthUser): AuthUser {
  return { ...user };
}

function mirrorServerUser(id: string, handle: string): AuthUser {
  const users = readUsers();
  const email = normalizeEmail(handle);
  const existing = Object.values(users).find((u) => u.id === id);
  if (existing) {
    // Always sync handle from the server — shared devices can leave a stale
    // local email keyed to the same user id (wrong account shown in the UI).
    if (existing.email !== email) {
      delete users[existing.email];
      const updated: AuthUser = { ...existing, email };
      users[email] = updated;
      writeUsers(users);
      writeSession({ userId: updated.id, email: updated.email });
      return updated;
    }
    writeSession({ userId: existing.id, email: existing.email });
    return existing;
  }
  // Drop any leftover row that still claims this email under a different id.
  const staleByEmail = users[email];
  if (staleByEmail && staleByEmail.id !== id) {
    delete users[email];
  }
  const user: AuthUser = {
    id,
    email,
    passwordHash: "",
    salt: "",
    createdAt: new Date().toISOString(),
  };
  users[email] = user;
  writeUsers(users);
  writeSession({ userId: user.id, email: user.email });
  return user;
}

export type RegisterResult =
  | (AuthResult & { recoveryCode?: string; recoveryHint?: string })
  | AuthResult;

/**
 * Dual-mode auth: Cloudflare cookie session when /api is healthy,
 * otherwise the existing local email/password prototype (un8.* keys unchanged).
 * "Handle" is stored in the email field for local compatibility.
 */
export const authStore = {
  subscribe(fn: Listener): () => void {
    listeners.add(fn);
    return () => listeners.delete(fn);
  },

  getSession(): AuthSession | null {
    const session = readSession();
    if (!session) return null;
    if (getAuthMode() === "server") return session;
    const user = this.getUserById(session.userId);
    if (!user) {
      writeSession(null);
      return null;
    }
    return { userId: user.id, email: user.email };
  },

  getUserById(userId: string): AuthUser | null {
    return Object.values(readUsers()).find((u) => u.id === userId) ?? null;
  },

  getUserByEmail(email: string): AuthUser | null {
    return readUsers()[normalizeEmail(email)] ?? null;
  },

  isLoggedIn(): boolean {
    return Boolean(this.getSession());
  },

  async register(email: string, password: string): Promise<RegisterResult> {
    if (await apiHealth()) {
      const res = await apiJson<{
        ok: boolean;
        user: { id: string; handle: string; isModerator: boolean };
        recoveryCode?: string;
        recoveryHint?: string;
        error?: string;
      }>("/api/auth/register", "POST", { handle: email, passphrase: password });
      if (!res.ok) return { ok: false, error: res.error };
      setMode("server");
      const user = mirrorServerUser(res.data.user.id, res.data.user.handle);
      try {
        if (res.data.recoveryCode) {
          sessionStorage.setItem(RECOVERY_SHOWN_KEY, res.data.recoveryCode);
        }
      } catch {
        /* ignore */
      }
      return {
        ok: true,
        user: toPublic(user),
        recoveryCode: res.data.recoveryCode,
        recoveryHint: res.data.recoveryHint,
      };
    }

    const normalized = normalizeEmail(email);
    if (!isValidEmail(normalized) && !/^[a-z0-9._+-]{3,40}$/i.test(normalized)) {
      return { ok: false, error: "Enter a valid handle (or email)." };
    }
    if (!isValidPassword(password)) {
      return { ok: false, error: "Passphrase must be at least 8 characters." };
    }

    const users = readUsers();
    if (users[normalized]) {
      return { ok: false, error: "An account with this handle already exists. Log in instead." };
    }

    const salt = randomSalt();
    const passwordHash = await hashPassword(password, salt);
    const user: AuthUser = {
      id: newUserId(),
      email: normalized,
      passwordHash,
      salt,
      createdAt: new Date().toISOString(),
    };
    users[normalized] = user;
    writeUsers(users);
    setMode("local");
    writeSession({ userId: user.id, email: user.email });
    return { ok: true, user: toPublic(user) };
  },

  async login(email: string, password: string): Promise<AuthResult> {
    if (await apiHealth()) {
      const res = await apiJson<{
        ok: boolean;
        user: { id: string; handle: string; isModerator: boolean };
      }>("/api/auth/login", "POST", { handle: email, passphrase: password });
      if (!res.ok) return { ok: false, error: res.error };
      setMode("server");
      const user = mirrorServerUser(res.data.user.id, res.data.user.handle);
      return { ok: true, user: toPublic(user) };
    }

    const normalized = normalizeEmail(email);
    if ((!isValidEmail(normalized) && normalized.length < 3) || !password) {
      return { ok: false, error: "Enter handle and passphrase." };
    }
    const user = readUsers()[normalized];
    if (!user) {
      return { ok: false, error: "No account found for that handle." };
    }
    const ok = await verifyPassword(password, user.salt, user.passwordHash);
    if (!ok) {
      return { ok: false, error: "Incorrect passphrase." };
    }
    setMode("local");
    writeSession({ userId: user.id, email: user.email });
    return { ok: true, user: toPublic(user) };
  },

  async logout(): Promise<void> {
    if (getAuthMode() === "server") {
      await apiJson("/api/auth/logout", "POST");
    }
    setMode("local");
    writeSession(null);
  },

  getUserId(): string | null {
    return this.getSession()?.userId ?? null;
  },

  getCurrentUser(): AuthUser | null {
    const id = this.getUserId();
    return id ? this.getUserById(id) : null;
  },

  async ensureRegistered(email: string, password: string): Promise<RegisterResult> {
    if (await apiHealth()) {
      const me = await apiJson<{ ok: boolean; user?: { id: string; handle: string } }>(
        "/api/me",
        "GET"
      );
      if (me.ok && me.data.user) {
        setMode("server");
        const user = mirrorServerUser(me.data.user.id, me.data.user.handle);
        return { ok: true, user: toPublic(user) };
      }
      const normalized = normalizeEmail(email);
      // Try login first if handle might exist
      const loginTry = await this.login(normalized, password);
      if (loginTry.ok) return loginTry;
      return this.register(normalized, password);
    }

    const normalized = normalizeEmail(email);
    if (this.getUserByEmail(normalized)) {
      return this.login(normalized, password);
    }
    return this.register(normalized, password);
  },

  continueAsGuest(gender: "male" | "female"): AuthUser {
    let install = "";
    try {
      install = localStorage.getItem("un8.auth.installId.v1") || "";
      if (!install) {
        install = `inst_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
        localStorage.setItem("un8.auth.installId.v1", install);
      }
    } catch {
      install = `inst_${Date.now().toString(36)}`;
    }

    const id = `user_guest_${install}_${gender}`;
    const email = `${id}@guest.un8.local`;
    const users = readUsers();
    const existing = Object.values(users).find((u) => u.id === id);
    if (existing) {
      setMode("local");
      writeSession({ userId: existing.id, email: existing.email });
      return existing;
    }

    const user: AuthUser = {
      id,
      email,
      passwordHash: "",
      salt: "",
      createdAt: new Date().toISOString(),
    };
    users[email] = user;
    writeUsers(users);
    setMode("local");
    writeSession({ userId: user.id, email: user.email });
    return user;
  },

  takeRecoveryCode(): string | null {
    try {
      const code = sessionStorage.getItem(RECOVERY_SHOWN_KEY);
      if (code) sessionStorage.removeItem(RECOVERY_SHOWN_KEY);
      return code;
    } catch {
      return null;
    }
  },

  async deleteAccount(): Promise<{ ok: true } | { ok: false; error: string }> {
    if (!(await apiHealth()) || getAuthMode() !== "server") {
      return {
        ok: false,
        error: "Account delete requires the live server. Sign in while online and try again.",
      };
    }
    const res = await apiJson<{ ok: boolean; error?: string }>("/api/account", "DELETE");
    if (!res.ok) return { ok: false, error: res.error };
    writeSession(null);
    setMode("local");
    return { ok: true };
  },

  async refreshFromServer(): Promise<boolean> {
    if (!(await apiHealth())) return false;
    const me = await apiJson<{
      ok: boolean;
      user?: { id: string; handle: string; isModerator: boolean };
    }>("/api/me", "GET");
    if (!me.ok || !me.data.user) return false;
    setMode("server");
    mirrorServerUser(me.data.user.id, me.data.user.handle);
    return true;
  },
};
